import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  selectActiveOrganizationId,
  selectIsSuperAdmin,
  selectMemberships,
  setActiveOrganization,
  setMemberships,
} from "../../redux/authSlice";
import {
  getMyMemberships,
  listTenantOrganizations,
  switchOrganization,
} from "../../services/organizationService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import YhSelect from "../../ui/YhSelect";

/**
 * Shown when user has multiple assigned orgs OR is platform super admin.
 * Single-org users see a read-only label for their assigned organization.
 *
 * After switch: persist new token + org, then full reload so all screens
 * refetch with the new x-organization-id header.
 */
const OrgSwitcher: React.FC = () => {
  const dispatch = useDispatch();
  const memberships = useSelector(selectMemberships);
  const activeOrganizationId = useSelector(selectActiveOrganizationId);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const [loading, setLoading] = useState(false);
  const [tenantOrgs, setTenantOrgs] = useState<
    { id: string; label: string; status?: string }[]
  >([]);

  // Super admins: load full tenant list (matches Settings → Organization page).
  // Header used to show only login memberships, so new orgs (e.g. VKS) were missing.
  useEffect(() => {
    if (!isSuperAdmin) {
      setTenantOrgs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await listTenantOrganizations();
        if (cancelled || res?.success !== 1) return;
        const raw = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];
        const list = raw
          .map((o: any) => {
            const id = String(o?._id || o?.id || "").trim();
            if (!id || id === "undefined") return null;
            return {
              id,
              label:
                o?.name || o?.brandName || o?.legalName || o?.slug || id,
              status: o?.status || "active",
            };
          })
          .filter(Boolean) as { id: string; label: string; status?: string }[];
        if (!cancelled) setTenantOrgs(list);
      } catch (_) {
        if (!cancelled) setTenantOrgs([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  // Keep Redux memberships in sync (assign/remove happens elsewhere)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getMyMemberships();
        if (cancelled || res?.success !== 1) return;
        const rows = Array.isArray(res.data) ? res.data : [];
        const next = rows
          .filter((m: any) => m && m.status !== "inactive")
          .map((m: any) => ({
            organizationId: String(m.organizationId),
            roleSlug: m.roleSlug,
            canViewAuditLogs: !!m.canViewAuditLogs,
            isPrimary: !!m.isPrimary,
            organization: m.organization
              ? {
                  _id: String(m.organization._id),
                  name:
                    m.organization.name ||
                    m.organization.brandName ||
                    m.organization.legalName ||
                    "",
                  slug: m.organization.slug || "",
                  brandName: m.organization.brandName || "",
                  status: m.organization.status || "active",
                  logoUrl: m.organization.logoUrl || "",
                }
              : null,
          }));
        const hasSuperMembership = next.some(
          (m: any) => m.roleSlug === "super_admin"
        );
        const hasTenantOnly =
          next.length > 0 &&
          next.every((m: any) => m.roleSlug !== "super_admin");
        const scopedSuper = hasSuperMembership
          ? true
          : hasTenantOnly
            ? false
            : isSuperAdmin;

        const prevActive = activeOrganizationId;
        let nextActive = activeOrganizationId;
        if (!scopedSuper) {
          const allowed = new Set(next.map((m: any) => m.organizationId));
          if (
            !nextActive ||
            nextActive === "all" ||
            nextActive === "*" ||
            !allowed.has(String(nextActive))
          ) {
            const primary =
              next.find((m: any) => m.isPrimary) || next[0] || null;
            nextActive = primary ? primary.organizationId : null;
          }
        }

        dispatch(
          setMemberships({
            memberships: next,
            isSuperAdmin: scopedSuper,
            activeOrganizationId: nextActive,
          })
        );

        // Stale sessions often had activeOrganizationId=all; force one reload
        // so list APIs pick up x-organization-id for the assigned org.
        const wasAll =
          prevActive === "all" || prevActive === "*" || !prevActive;
        const nowScoped =
          nextActive && nextActive !== "all" && nextActive !== "*";
        if (
          !scopedSuper &&
          wasAll &&
          nowScoped &&
          !sessionStorage.getItem("org_scope_reloaded")
        ) {
          sessionStorage.setItem("org_scope_reloaded", "1");
          window.location.reload();
        }
      } catch (_) {
        /* keep cached memberships */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh once on mount
  }, [dispatch]);

  const options = useMemo(() => {
    if (isSuperAdmin && tenantOrgs.length) {
      const seen = new Set<string>();
      const unique = tenantOrgs.filter((o) => {
        if (seen.has(o.id)) return false;
        seen.add(o.id);
        return true;
      });
      return [{ id: "all", label: "All organizations" }, ...unique];
    }

    const list = (memberships || [])
      .filter((m) => m.organization || m.organizationId)
      .map((m) => ({
        id: String(m.organizationId),
        label:
          m.organization?.name ||
          m.organization?.brandName ||
          m.organization?.slug ||
          String(m.organizationId),
      }));
    const seen = new Set<string>();
    const unique = list.filter((o) => {
      if (seen.has(o.id)) return false;
      seen.add(o.id);
      return true;
    });
    if (isSuperAdmin) {
      return [{ id: "all", label: "All organizations" }, ...unique];
    }
    return unique;
  }, [memberships, isSuperAdmin, tenantOrgs]);

  const selectOptions = useMemo(
    () => options.map((o) => ({ label: o.label, value: o.id })),
    [options]
  );

  const handleChange = async (value: string) => {
    if (String(value) === String(activeOrganizationId)) return;
    try {
      setLoading(true);
      const res = await switchOrganization(value);
      const token = res?.data?.accessToken;
      dispatch(
        setActiveOrganization({
          activeOrganizationId: value,
          ...(token ? { accessToken: token } : {}),
        })
      );
      const label =
        value === "all"
          ? "All organizations"
          : options.find((o) => o.id === value)?.label || "Organization";
      try {
        sessionStorage.setItem("active_organization_label", label);
        sessionStorage.setItem("active_organization_id", String(value));
      } catch (_) {}
      openSuccessNotification(`Switched to ${label}. Reloading data…`);
      setTimeout(() => {
        window.location.reload();
      }, 300);
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to switch organization");
      setLoading(false);
    }
  };

  const currentValue = useMemo(() => {
    if (!options.length) return "";
    if (
      activeOrganizationId &&
      options.some((o) => o.id === String(activeOrganizationId))
    ) {
      return String(activeOrganizationId);
    }
    if (isSuperAdmin) return "all";
    return options[0]?.id || "";
  }, [options, activeOrganizationId, isSuperAdmin]);

  const selectedLabel = useMemo(() => {
    if (!options.length) return "";
    if (!isSuperAdmin && options.length === 1) return options[0].label;
    return (
      options.find((o) => o.id === currentValue)?.label ||
      (currentValue === "all" ? "All organizations" : "Organization")
    );
  }, [options, currentValue, isSuperAdmin]);

  useEffect(() => {
    if (!currentValue || !selectedLabel) return;
    try {
      sessionStorage.setItem("active_organization_label", selectedLabel);
      sessionStorage.setItem("active_organization_id", String(currentValue));
    } catch (_) {}
  }, [currentValue, selectedLabel]);

  if (!options.length) {
    return null;
  }

  // Single assigned org — show label only (no "All organizations")
  if (!isSuperAdmin && options.length === 1) {
    return (
      <Box
        sx={{
          minWidth: 140,
          mr: 2,
          px: 1.5,
          py: 0.75,
          borderRadius: "10px",
          border: "1px solid #E5E7EB",
          bgcolor: "#F9F0F4",
        }}
        title={options[0].label}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: "#111827",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 220,
          }}
        >
          {options[0].label}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minWidth: 220,
        maxWidth: 300,
        mr: 2,
      }}
    >
      <YhSelect
        label=""
        placeholder="Select organization"
        searchPlaceholder="Search organizations…"
        searchable
        showOptionIcon={false}
        disabled={loading}
        value={currentValue}
        options={selectOptions}
        onChange={(e) => {
          const next = String(e?.target?.value || "");
          if (next) handleChange(next);
        }}
      />
    </Box>
  );
};

export default OrgSwitcher;
